import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HERO_IMAGE, KITCHEN_IMAGES, BATHROOM_IMAGES, CARPENTRY_IMAGES, BASEMENT_IMAGES, TRUST_STATS, PROCESS_STEPS, TESTIMONIALS, COMPANY, HERO_HOTSPOTS, TRANSFORM_STAGES } from '../content';

const DUST = Array.from({ length: 16 }, (_, i) => ({
  left: (i * 61 + 7) % 100,
  top: (i * 37 + 11) % 100,
  dur: 9 + (i % 7) * 2,
  delay: (i % 6) * 1.3,
  scale: 0.5 + (i % 4) * 0.35,
}));

function ServiceCard({ to, eyebrow, title, body, image }) {
  return (
    <Link to={to} className="gl-tile group block aspect-[3/4] relative overflow-hidden gl-reveal">
      <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-black/30" />
      <div className="absolute inset-x-0 bottom-0 p-8">
        <div className="gl-overline mb-2">{eyebrow}</div>
        <h3 className="gl-serif text-4xl md:text-5xl text-white leading-none">{title}</h3>
        <p className="mt-4 text-sm text-[color:var(--gl-muted)] max-w-xs leading-relaxed">{body}</p>
        <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white">See work <span className="text-[color:var(--gl-primary)]">→</span></div>
      </div>
    </Link>
  );
}

export default function GreenlineHome() {
  const heroRef = useRef(null);

  // Mouse-responsive parallax depth
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    let raf = null;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty('--px', x.toFixed(3));
        el.style.setProperty('--py', y.toFixed(3));
      });
    };
    el.addEventListener('mousemove', onMove);
    return () => { el.removeEventListener('mousemove', onMove); if (raf) cancelAnimationFrame(raf); };
  }, []);

  return (
    <>
      <section ref={heroRef} className="gl-hero relative h-[100svh] min-h-[640px] flex items-end overflow-hidden">
        {/* Cinematic video (parallax depth layer) */}
        <div className="gl-hero-media">
          <video className="gl-hero-video" poster={HERO_IMAGE} autoPlay muted loop playsInline preload="auto" aria-hidden="true">
            <source src="/hero-kitchen-walk.mp4?v=walk" type="video/mp4" />
          </video>
        </div>

        {/* Flowing caustic light */}
        <svg className="gl-hero-caustics" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <defs>
            <radialGradient id="gl-caustic-grad" cx="58%" cy="42%" r="80%">
              <stop offset="0%" stopColor="rgba(255,214,158,0.6)" />
              <stop offset="40%" stopColor="rgba(255,168,88,0.2)" />
              <stop offset="100%" stopColor="rgba(0,0,0,0)" />
            </radialGradient>
            <filter id="gl-caustic-filter" x="-20%" y="-20%" width="140%" height="140%">
              <feTurbulence type="fractalNoise" baseFrequency="0.012 0.018" numOctaves="2" seed="7" stitchTiles="stitch" result="noise">
                <animate attributeName="baseFrequency" dur="24s" values="0.012 0.018;0.02 0.011;0.012 0.018" repeatCount="indefinite" />
              </feTurbulence>
              <feDisplacementMap in="SourceGraphic" in2="noise" scale="22" xChannelSelector="R" yChannelSelector="G" />
            </filter>
          </defs>
          <rect width="100" height="100" fill="url(#gl-caustic-grad)" filter="url(#gl-caustic-filter)" />
        </svg>

        {/* Floating dust / light particles */}
        <div className="gl-hero-particles" aria-hidden="true">
          {DUST.map((d, i) => (
            <span key={i} className="gl-dust" style={{ left: `${d.left}%`, top: `${d.top}%`, animationDuration: `${d.dur}s`, animationDelay: `${d.delay}s`, transform: `scale(${d.scale})` }} />
          ))}
        </div>

        {/* Liquid sheen sweep */}
        <div className="gl-hero-sheen" aria-hidden="true" />

        <div className="gl-video-veil" />

        {/* Interactive hotspots */}
        <div className="gl-hero-hotspots" aria-hidden="false">
          {HERO_HOTSPOTS.map((h) => (
            <Link key={h.to} to={h.to} className="gl-hotspot" style={{ left: h.x, top: h.y }} data-testid={`hero-hotspot-${h.label.toLowerCase()}`} aria-label={`${h.label} — ${h.detail}`}>
              <span className="gl-hotspot-dot" />
              <span className="gl-hotspot-card">
                <span className="gl-hotspot-title">{h.label}</span>
                <span className="gl-hotspot-detail">{h.detail}</span>
              </span>
            </Link>
          ))}
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pb-20 lg:pb-28 w-full">
          <div className="gl-overline mb-6 gl-reveal"><span className="gl-hairline" />Massachusetts Carpenters</div>
          <h1 className="gl-serif text-white text-4xl sm:text-6xl lg:text-8xl leading-[0.95] max-w-4xl gl-reveal" style={{ transitionDelay: '0.1s' }}>
            Transforming Homes Into <em className="not-italic" style={{ color: 'var(--gl-primary)' }}>Masterpieces.</em>
          </h1>
          <p className="mt-7 max-w-xl text-base sm:text-lg text-[color:var(--gl-fg)]/80 leading-relaxed gl-reveal" style={{ transitionDelay: '0.18s' }}>
            Kitchens, bathrooms, basements, and custom carpentry across Eastern Massachusetts.
          </p>
          <div className="mt-9 flex flex-wrap gap-3 gl-reveal" style={{ transitionDelay: '0.26s' }}>
            <a href="#transformations" className="gl-btn-primary gl-btn-shine" data-testid="hero-cta-explore">Explore Transformations →</a>
            <Link to="/contact" className="gl-btn-secondary" data-testid="hero-cta-consult">Schedule Consultation</Link>
          </div>
        </div>
      </section>

      {/* Transformation Timeline */}
      <section id="transformations" className="gl-timeline py-24 sm:py-32 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14 gl-reveal">
            <div className="gl-overline mb-4">The Transformation</div>
            <h2 className="gl-serif text-white text-4xl sm:text-6xl leading-none">From first cut to final reveal.</h2>
            <p className="mt-5 text-[color:var(--gl-muted)] max-w-2xl mx-auto">Every project moves through the same disciplined stages — so you always know exactly where things stand.</p>
          </div>
          <div className="gl-timeline-track">
            {TRANSFORM_STAGES.map((s, i) => (
              <div key={s.step} className="gl-stage gl-reveal" style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="gl-stage-media">
                  <img src={s.img} alt={s.title} loading="lazy" />
                  <span className="gl-stage-step">{s.step}</span>
                  {i < TRANSFORM_STAGES.length - 1 && <span className="gl-stage-arrow" aria-hidden="true">→</span>}
                </div>
                <h3 className="gl-serif text-2xl text-white mt-5">{s.title}</h3>
                <p className="text-sm text-[color:var(--gl-muted)] leading-relaxed mt-2">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center gl-reveal">
          <div className="gl-overline mb-6">A Different Kind of Contractor</div>
          <p className="gl-serif text-2xl sm:text-3xl md:text-4xl leading-snug text-[color:var(--gl-fg)]">
            Most renovation companies subcontract everything. We don&apos;t. The same crew that frames the wall installs your cabinets, fits your trim, and shakes your hand at the reveal.
          </p>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div className="gl-reveal">
              <div className="gl-overline mb-4"><span className="gl-hairline" />What We Do</div>
              <h2 className="gl-serif text-white text-4xl sm:text-6xl leading-none">Four crafts, one studio.</h2>
            </div>
            <Link to="/contact" className="text-xs uppercase tracking-[0.2em] text-white border-b border-[color:var(--gl-primary)] pb-1 hover:text-[color:var(--gl-primary)]">Start a project →</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <ServiceCard to="/kitchens" eyebrow="01 · Kitchens" title="Heirloom Kitchens" body="Full gut renovations with custom cabinetry, integrated appliances, and millwork that won't go out of style in a decade." image={KITCHEN_IMAGES[0].src} />
            <ServiceCard to="/bathrooms" eyebrow="02 · Bathrooms" title="Sanctuary Baths" body="Spa-grade primary suites and powder rooms. Curbless showers, slab-vein matching, brass and natural stone." image={BATHROOM_IMAGES[0].src} />
            <ServiceCard to="/carpentry" eyebrow="03 · Carpentry" title="Carpentry & Built-Ins" body="Built-ins, trim and crown molding, closets, mudrooms, mantels, stair railings, decks, and exterior trim." image={CARPENTRY_IMAGES[0].src} />
            <ServiceCard to="/basements" eyebrow="04 · Basements" title="Finished Basements" body="Family rooms, media lounges, gyms, and guest suites — warm, dry, daylight-bright, and built to code." image={BASEMENT_IMAGES[0].src} />
          </div>
        </div>
      </section>

      <section className="border-t border-b border-white/10 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4">
          {TRUST_STATS.map((s, i) => (
            <div key={s.label} className={`text-center p-6 md:p-8 gl-reveal ${i < 3 ? 'gl-rule-right' : ''}`} style={{ transitionDelay: `${i * 0.08}s` }}>
              <div className="gl-serif text-3xl sm:text-4xl" style={{ color: 'var(--gl-primary)' }}>{s.value}</div>
              <div className="gl-overline mt-3 text-[color:var(--gl-muted)]">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16 gl-reveal">
            <div className="gl-overline mb-4">Our Process</div>
            <h2 className="gl-serif text-white text-4xl sm:text-6xl">From first walk-through to reveal.</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-10">
            {PROCESS_STEPS.map((step, i) => (
              <div key={step.step} className="relative gl-reveal" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="gl-serif text-7xl text-white/10 mb-4">{step.step}</div>
                <h3 className="gl-serif text-2xl text-white mb-3">{step.title}</h3>
                <p className="text-sm text-[color:var(--gl-muted)] leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-28 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="gl-overline mb-10 gl-reveal">Word of Mouth</div>
          <div className="grid md:grid-cols-2 gap-12">
            {TESTIMONIALS.map((t, i) => (
              <figure key={t.name} className="gl-reveal" style={{ transitionDelay: `${i * 0.1}s` }}>
                <blockquote className="gl-serif text-2xl sm:text-3xl leading-snug text-white">&ldquo;{t.quote}&rdquo;</blockquote>
                <figcaption className="mt-6 text-sm text-[color:var(--gl-muted)]"><span className="text-white">{t.name}</span> · {t.project}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="py-32 border-t border-white/5 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <div className="gl-overline mb-6 gl-reveal">Ready When You Are</div>
          <h2 className="gl-serif text-white text-5xl sm:text-7xl leading-none mb-8 gl-reveal" style={{ transitionDelay: '0.1s' }}>
            Let&apos;s design something <em className="not-italic" style={{ color: 'var(--gl-primary)' }}>worth living in.</em>
          </h2>
          <p className="text-base text-[color:var(--gl-muted)] mb-10 max-w-xl mx-auto gl-reveal" style={{ transitionDelay: '0.18s' }}>
            We typically book six to ten weeks out. A 30-minute consult costs nothing and tells you everything.
          </p>
          <div className="flex flex-wrap gap-4 justify-center gl-reveal" style={{ transitionDelay: '0.26s' }}>
            <Link to="/contact" className="gl-btn-primary">Start the Conversation →</Link>
            <a href={COMPANY.phoneHref} className="gl-btn-secondary">{COMPANY.phone}</a>
          </div>
        </div>
      </section>
    </>
  );
}
